import { Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import React from 'react';

import './HomePage.css';
import { useAuth } from '@/shared/contexts/AuthContext';
import SEO from '@/components/common/SEO';
import babyBili from '@/assets/img/icons/svg/babyBili.svg';
import globeAmericas from '@/assets/img/icons/svg/globeAmericas.svg';
import peopleIcon from '@/assets/img/icons/svg/peopleIcon.svg';
import searchIcon from '@/assets/img/icons/svg/search.svg';

import HomeHeroSlideshow from './components/HomeHeroSlideshow';
import HomeFeaturePreview from './components/HomeFeaturePreview';
import SustainabilityPillarsPreview from './components/SustainabilityPillarsPreview';

// Resolve the carousel images from webpack's require.context build-time API.
// Exported and accepted as a prop so the carousel rendering is testable: the
// require.context API does not exist in the Jest runtime, so callers can inject
// a list, and absent the API the carousel degrades to empty instead of throwing.
export function loadCarouselImages() {
    // require.context is a webpack build-time API. Under the Jest runtime it is
    // undefined, so the call throws and we return an empty list (tests inject a
    // fixed carouselImages list instead). It must be called directly with
    // literal arguments so webpack can statically extract the image set; a
    // `typeof require.context` guard makes `require` non-statically-analyzable
    // and emits a "Critical dependency" warning that fails the production build.
    try {
        const requireContext = require.context(
            '../../assets/img/carousel',
            false,
            /\.(png|jpe?g|svg)$/
        );
        /* istanbul ignore next -- webpack-only mapping; require.context throws
           under Jest, so this line is never reached in tests. */
        return requireContext.keys().map((key, index) => ({
            id: index + 1,
            src: requireContext(key),
        }));
    } catch {
        return [];
    }
}

const features = [
    {
        title: 'Bili Chat',
        description:
            'Ask Bili about sustainability in Colorado — local resources, environmental data, and what’s happening in your community.',
        icon: babyBili,
        iconFullColor: true,
        iconVariant: 'primary',
        path: '/bili-chat',
        authRequired: true,
        cta: 'Explore',
    },
    {
        title: 'Spotlights',
        description:
            'Deep dives into Colorado sustainability topics like wildfires, water, and data centers — with real data, analysis, and community discussion.',
        icon: globeAmericas,
        iconVariant: 'danger',
        path: '/spotlights',
        authRequired: false,
        cta: 'Explore',
    },
    {
        title: 'Communities of Interest',
        description:
            'Join topic-based groups to discuss sustainability, share resources, and connect with others across Colorado.',
        icon: peopleIcon,
        iconVariant: 'info',
        path: '/communities',
        authRequired: false,
        cta: 'Explore',
    },
    {
        title: 'Data Discovery',
        description:
            'Browse curated environmental datasets, data products, and organizations powering sustainability research.',
        icon: searchIcon,
        iconVariant: 'secondary',
        path: '/data',
        authRequired: false,
        cta: 'Explore',
    },
    {
        title: 'Advanced Query',
        description:
            'Build custom queries to search sustainability data with filters, keywords, and expert tools.',
        icon: null,
        bootstrapIcon: 'bi-sliders',
        iconVariant: 'warning',
        path: '/advanced-query',
        authRequired: true,
        cta: 'Explore',
    },
];

export default function HomePage({ carouselImages }) {
    const { user } = useAuth();

    const imgSrcList = carouselImages ?? loadCarouselImages();

    const seoDescription =
        'Sustainability Hub empowers communities with data-driven insights for ' +
        'environmental sustainability, economic prosperity, and well-being. ' +
        'Explore sustainability data and connect with experts.';

    function getFeatureLink(feature) {
        if (feature.authRequired && !user) {
            return '/signup';
        }
        return feature.path;
    }

    return (
        <section id="homepage" className="page-container">
            <SEO
                title="Home"
                description={seoDescription}
                canonical="/"
                isHomePage
                breadcrumbs={[{ name: 'Home', path: '/' }]}
            />

            {/* Hero Section */}
            <section className="home-hero">
                <HomeHeroSlideshow images={imgSrcList} />
                <div className="home-hero-content">
                    <Container>
                        <h1 className="display-4 text-white fw-bold mb-3">
                            Sustainability Hub
                        </h1>
                        <p className="lead text-white mb-4 home-hero-subtitle">
                            Making Colorado&apos;s environmental, social, and
                            economic data easy to explore, and accessible to
                            everyone.
                        </p>
                        {!user ? (
                            <div className="d-flex flex-column flex-sm-row gap-2">
                                <Link
                                    to="/signup"
                                    className="btn btn-warning text-primary btn-lg"
                                >
                                    Create Account
                                </Link>
                                <p className="text-white mb-0 align-self-sm-center">
                                    Already have an account?{' '}
                                    <Link
                                        to="/login"
                                        className="text-white fw-semibold home-hero-inline-link"
                                    >
                                        Log in
                                    </Link>
                                </p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column flex-sm-row gap-2">
                                <Link
                                    to="/dashboard"
                                    className="btn btn-primary btn-lg"
                                >
                                    Go to Dashboard
                                </Link>
                                <Link
                                    to="/bili-chat"
                                    className="btn btn-outline-light btn-lg"
                                >
                                    Chat with Bili
                                </Link>
                            </div>
                        )}
                    </Container>
                </div>
            </section>

            {/* Feature Highlights Section */}
            <section className="py-5">
                <Container>
                    <h2 className="text-primary fw-bold text-center mb-5">
                        Explore the Hub
                    </h2>
                    <div>
                        {features.map((feature, index) => (
                            <React.Fragment key={feature.title}>
                                <HomeFeaturePreview
                                    title={feature.title}
                                    description={feature.description}
                                    ctaLabel={feature.cta}
                                    to={getFeatureLink(feature)}
                                    icon={feature.icon}
                                    iconFullColor={feature.iconFullColor}
                                    bootstrapIcon={feature.bootstrapIcon}
                                    iconVariant={feature.iconVariant}
                                />
                                {index < features.length - 1 && (
                                    <hr className="home-feature-divider" />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Sustainability Pillars Preview */}
            <SustainabilityPillarsPreview />

            {/* Mission Statement Section */}
            <section className="py-5">
                <Container className="text-center">
                    <p className="lead text-info mb-3">
                        Integrating health, well-being, and environmental data
                        to foster collaboration across Colorado for a
                        sustainable future.
                    </p>
                    <p className="text-muted mx-auto homepage-mission-detail">
                        Built by students and faculty across five Colorado
                        universities, the Sustainability Hub combines
                        conversational AI, curated data, and community tools to
                        make sustainability data accessible to everyone.
                    </p>
                </Container>
            </section>
        </section>
    );
}

HomePage.propTypes = {
    // Optional override for the carousel image list. Defaults to the
    // webpack-resolved set; supplied in tests where require.context is absent.
    carouselImages: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            src: PropTypes.string,
        })
    ),
};
